import type { ButtonHTMLAttributes, ReactNode } from "react";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "warning";
  children: ReactNode;
}

/**
 * Button — v17 `.btn` primitive (lines 117–122 of the prototype).
 *
 * Common: 8px 14px padding, 8px radius (NOT the panel's 12px var(--radius)),
 * 12px font, 600 weight, inline-flex centered with 6px gap for an icon.
 *
 * Tokenized: 8px radius → `rounded-md` (the new semantic scale's 8px;
 * the old `rounded-lg` was Tailwind's default 8px — same pixels, now a
 * named token). `text-xs` (12px) + `font-semibold` → `text-label`
 * (12/1.4) with `font-semibold` overriding the role's default 500 weight
 * per the design spec's weight-override allowance.
 *
 * Primary: bg var(--accent), text #0a0a0a (bg), hover var(--accent-hover).
 * Secondary: bg var(--surface), color var(--text), 1px border var(--border);
 * hover flips border + text to accent.
 *
 * SECONDARY'S LABEL IS `--text`, NOT `--text-secondary`, and the rename is a bug
 * fix rather than a preference. Jared, 2026-08-25, on the Refresh button: *"in
 * dark mode, this looks like a deactive button. Can you see how light gray that
 * is? … they look inactive because they're so light gray. In light mode you can
 * see they're fine. I don't know why they're even different."* The why is the
 * token: `--text-secondary` is #888 on dark against a #141414 surface (~5.2:1)
 * and #555 on light against #fff (~7.0:1), so the same utility renders a
 * noticeably weaker label on the dark theme — which is the default theme. On top
 * of that, the disabled state is `opacity-40` of the SAME colour, so enabled and
 * disabled were two shades of grey a reader had to compare against nothing.
 *
 * `--text-secondary` is the token for meta and fine-print; the label of a live
 * control is primary copy. Moving to `--text` reads as enabled on both themes
 * (19:1 dark, 17:1 light) and gives `disabled:opacity-40` a real difference to
 * express. Same shape as the `text-warning-ink` fix in `ImportPicker.tsx` — the
 * wrong semantic token rather than a badly-chosen colour, so the fix is the
 * right token and not a hand-picked grey.
 * Danger: v17 `.btn-danger` (line 123) — a destructive red ghost button for
 * irreversible actions: bg-error-muted, text-error, 1px border-error-strong,
 * hover deepens the fill. Used by View 3's "Stop Sequence + Log Outreach".
 * Warning: solid amber fill with #0a0a0a (bg) text — the `primary` shape with
 * accent→warning. The Baby Chops "Chop Data" action uses it (View 4 branding).
 *
 * Layout note: v17 declares `flex: 1` on both variants so they fill the
 * footer's flex row. We DON'T bake that in — `flex-1` is layout, not
 * style, and feature code controls it via the `className` prop
 * (e.g. `<Button className="flex-1">…</Button>`).
 */
export function Button({
  variant = "primary",
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-1.5 rounded-md px-3.5 py-2 text-label font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed";

  const variants = {
    primary:
      "bg-accent text-bg hover:bg-accent-hover disabled:opacity-40 disabled:hover:bg-accent",
    secondary:
      "border border-border bg-surface text-text hover:border-accent hover:text-accent disabled:opacity-40 disabled:hover:border-border disabled:hover:text-text",
    danger:
      "border border-error-strong bg-error-muted text-error hover:bg-error-muted-hover disabled:opacity-40 disabled:hover:bg-error-muted",
    warning:
      "bg-warning text-bg hover:bg-warning-hover disabled:opacity-40 disabled:hover:bg-warning",
  } as const;

  const classes = [base, variants[variant], className].filter(Boolean).join(" ");

  return (
    <button {...rest} disabled={disabled} className={classes}>
      {children}
    </button>
  );
}
